import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Background,
  Controls,
  Edge,
  MiniMap,
  Node,
  ReactFlow,
  addEdge,
  useEdgesState,
  useNodesInitialized,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ConditionNode,
  DefinitionPane,
  InternalNode,
  InternalNode as InternalNodeType,
  LeafNode,
  LogicalOperator,
  Outcome,
  PaneType,
  PaneTypes,
  Representation,
  RulePane,
} from "@unlikelyai-magic/shared/data-access/api";
import { cn } from "@unlikelyai-magic/utils";
import { getLayoutedElements } from "./layout";
import { BlurbNode } from "./nodes/BlurbNode";
import { ConditionNodeSimple } from "./nodes/ConditionNodeSimple";
import { CustomEdge } from "./nodes/CustomEdge";
import { GroupNode } from "./nodes/GroupNode";
import { LogicalConnectiveNode } from "./nodes/LogicalConnectiveNode";
import { EditedConditionNode } from "./nodes/EditedConditionNode";
import { AutomaticEditConditionNode } from "./nodes/AutomaticEditConditionNode";
import { EditTraceNode } from "./nodes/EditTraceNode";
import {
  conditionNodeToRepresentation,
  RFNodeData,
  convertToRF,
  evaluateAnd,
  evaluateNot,
  evaluateOr,
} from "./utils";
export { UpdateType, type UpdatedNodeInfo } from "./types";
export { applyNodeUpdates } from "./editNodeUtils";

const edgeTypes = {
  "custom-edge": CustomEdge,
};

const nodeTypes = {
  connective: LogicalConnectiveNode,
  condition: ConditionNodeSimple,
  blurb: BlurbNode,
  group: GroupNode,
  edited: EditedConditionNode,
  automaticEditNode: AutomaticEditConditionNode,
  editTrace: EditTraceNode,
};

export interface LayoutFlowStaticProps {
  panes: PaneTypes[];
  questionAnswers: any[];
  className?: string;
  showMiniMap?: boolean;
  /**
   * Optional callback when a node is clicked.
   * Receives the node ID, node type, and pane type (for group nodes).
   */
  onNodeClick?: (nodeId: string, nodeType: string, paneType?: PaneType) => void;
  /**
   * Optional callback when the pane (background) is clicked.
   */
  onPaneClick?: () => void;
  /**
   * Pre-processed graph data. When provided, bypasses the pane conversion
   * and uses these nodes/edges directly. Use with applyNodeUpdates() for edit visualizations.
   */
  preProcessedGraph?: { nodes: Node[]; edges: Edge[] };
}

/**
 * Converts panes to React Flow nodes and edges.
 * Export for use with applyNodeUpdates() when pre-processing is needed.
 */
export const convertPanesToGraph = (
  panes: PaneTypes[],
  questionAnswers: any[]
): { nodes: Node[]; edges: Edge[] } => {
  return panes
    .filter(
      (pane) =>
        pane.paneType === PaneType.RULE ||
        pane.paneType === PaneType.DEFINITION
    )
    .reduce(
      (acc: { nodes: Node[]; edges: Edge[] }, pane: PaneTypes) => {
        return convertPaneToRF(pane, acc, questionAnswers);
      },
      { nodes: [], edges: [] }
    );
};

/**
 * Same as LayoutFlow but does not update the node values when the value prop changes.
 */
export const LayoutFlowStatic = ({
  panes,
  questionAnswers,
  className,
  showMiniMap = false,
  onNodeClick: onNodeClickProp,
  onPaneClick: onPaneClickProp,
  preProcessedGraph,
}: LayoutFlowStaticProps) => {
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    // Use pre-processed graph if provided, otherwise convert from panes
    if (preProcessedGraph) {
      return preProcessedGraph;
    }
    return convertPanesToGraph(panes, questionAnswers);
  }, [panes, questionAnswers, preProcessedGraph]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(initialEdges);

  const { fitView } = useReactFlow();

  const [isInitialLayout, setIsInitialLayout] = useState(false);
  const isInitialized = useNodesInitialized();

  const updateNodeInternals = useUpdateNodeInternals();

  const onLayout = useCallback(
    async (
      nodes: Node<RFNodeData>[],
      edges: Edge[],
      direction: "TB" | "LR"
    ) => {
      const layouted = await getLayoutedElements(nodes, edges, { direction });
      updateNodeInternals(nodes.map((node: any) => node.id));

      setNodes(layouted.nodes);
      setEdges(layouted.edges);

      // Use requestAnimationFrame to defer fitView and avoid ResizeObserver conflicts
      requestAnimationFrame(() => {
        setTimeout(() => {
          fitView({
            duration: 1000,
            // Ensure all panes are visible and nicely centered on initial load.
            // Let React Flow determine the zoom level needed to fit everything.
            padding: 0.2,
          });
        }, 200);
      });
    },
    [fitView, setEdges, setNodes, updateNodeInternals]
  );

  useEffect(() => {
    if (
      !isInitialLayout &&
      isInitialized &&
      nodes.length > 0 &&
      edges.length > 0
    ) {
      const timeoutId = setTimeout(async () => {
        await onLayout(nodes as Node<RFNodeData>[], edges, "TB");
        setIsInitialLayout(true);
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [isInitialLayout, isInitialized, nodes.length, edges.length, onLayout]);

  const onConnect = useCallback(
    (params: any) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const zoomToNode = useCallback(
    (node: Node) => {
      fitView({
        nodes: [node],
        duration: 800,
        padding: 0.2,
        maxZoom: 1,
        minZoom: 0.1,
      });
    },
    [fitView]
  );

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      // Call the optional callback if provided
      if (onNodeClickProp) {
        const paneType =
          node.type === "group" ? (node.data.type as PaneType) : undefined;
        onNodeClickProp(node.id, node.type ?? "", paneType);
      }
      // Zoom to the clicked node
      zoomToNode(node);
    },
    [onNodeClickProp, zoomToNode]
  );

  const onPaneClick = useCallback(() => {
    if (onPaneClickProp) {
      onPaneClickProp();
    }
    fitView({
      duration: 1000,
    });
  }, [onPaneClickProp, fitView]);

  return (
    <div className={cn("w-full h-full", className)}>
      <ReactFlow
        defaultViewport={{
          x: 0,
          y: 0,
          // Initial zoom.
          zoom: 0.25,
        }}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        minZoom={0.01}
        style={{
          transition: "opacity 0.5s ease-in-out",
        }}
      >
        <Controls />
        {showMiniMap && <MiniMap />}
        <Background gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

/**
 * Normalizes propagateWhen values to Outcome enum values.
 * Handles both string values (e.g., "TRUE") and Outcome enum values.
 */
const normalizePropagateWhen = (
  propagateWhen: Outcome[] | string[] | undefined
): Outcome[] | undefined => {
  if (!propagateWhen || !Array.isArray(propagateWhen)) return undefined;
  return propagateWhen.map((v) =>
    typeof v === "string" ? ((Outcome as any)[v] ?? v) : v
  ) as Outcome[];
};

/**
 * Checks if a child node's value should "count" toward its parent's evaluation
 * based on the propagateWhen constraint.
 * If propagateWhen is not set, the child always counts.
 * If propagateWhen is set, the child only counts if its value is in the list.
 */
const doesChildCount = (
  childValue: Outcome,
  propagateWhen: Outcome[] | string[] | undefined
): boolean => {
  const normalizedPropagateWhen = normalizePropagateWhen(propagateWhen);
  if (!normalizedPropagateWhen || normalizedPropagateWhen.length === 0) {
    return true; // No constraint, child always counts
  }
  return normalizedPropagateWhen.includes(childValue);
};

/**
 * Propagates values up the tree by computing parent node values based on their
 * children's values, respecting the propagateWhen constraint.
 *
 * The propagateWhen logic: if a child has propagateWhen set (e.g., ["FALSE"]),
 * that child only "counts" toward the parent's evaluation when its value is in
 * that list. Otherwise, the child is effectively ignored.
 */
const propagateValuesUp = (nodes: Node[], edges: Edge[]): Node[] => {
  // Build a map of node id to node for quick lookup
  const nodeMap = new Map<string, Node>();
  nodes.forEach((n) => nodeMap.set(n.id, n));

  // Build a map of parent id to child ids
  const parentToChildren = new Map<string, string[]>();
  edges.forEach((edge) => {
    const children = parentToChildren.get(edge.source) || [];
    children.push(edge.target);
    parentToChildren.set(edge.source, children);
  });

  // Recursive function to compute a node's value
  const computeNodeValue = (nodeId: string): Outcome => {
    const node = nodeMap.get(nodeId);
    if (!node) return Outcome.UNDECIDED;

    // If it's a group node, skip
    if (node.type === "group") return Outcome.UNDECIDED;

    const children = parentToChildren.get(nodeId) || [];

    // If no children (leaf node), return the current value
    if (children.length === 0) {
      return (node.data as RFNodeData).value ?? Outcome.UNDECIDED;
    }

    // Get child values, respecting propagateWhen
    const countingChildValues: Outcome[] = [];
    children.forEach((childId) => {
      const childNode = nodeMap.get(childId);
      if (!childNode || childNode.type === "group") return;

      const childValue = computeNodeValue(childId);
      const childData = childNode.data as RFNodeData;
      const propagateWhen = childData.propagateWhen;

      // Check if this child's value should count
      if (doesChildCount(childValue, propagateWhen)) {
        countingChildValues.push(childValue);
      }
    });

    // If no children count, the parent is true (all conditions are satisfied
    // or don't apply)
    if (countingChildValues.length === 0) {
      return Outcome.TRUE;
    }

    // Evaluate based on logical operator
    const nodeData = node.data as RFNodeData;
    const operator = nodeData.logicalOperator;

    switch (operator) {
      case LogicalOperator.AND:
        return evaluateAnd(countingChildValues);
      case LogicalOperator.OR:
        return evaluateOr(countingChildValues);
      case LogicalOperator.NOT:
        return evaluateNot(countingChildValues);
      default:
        return evaluateAnd(countingChildValues);
    }
  };

  // Compute values for all nodes and update them
  return nodes.map((node) => {
    if (node.type === "group") return node;

    const computedValue = computeNodeValue(node.id);
    return {
      ...node,
      data: {
        ...node.data,
        value: computedValue,
      },
    };
  });
};

/**
 * This method is called iteratively to build the React Flow nodes and edges from different pane types.
 * @returns Nodes and edges
 */
const convertPaneToRF = (
  pane: PaneTypes,
  acc: { nodes: Node[]; edges: Edge[] },
  questionAnswers: any[]
) => {
  const baseGroupElements = {
    type: "group",
    position: { x: 0, y: 0 },
  };
  const sanitizedId = pane.id;
  switch (pane.paneType) {
    case PaneType.RULE: {
      const rulePane = pane as RulePane;
      const ruleGroupNode = {
        id: sanitizedId,
        data: {
          productTreeId: rulePane.rule.name,
          type: pane.paneType,
          paneId: pane.id,
          blurbInformation: "",
          hasTree: true,
        },
        ...baseGroupElements,
      };
      const rootNodeTrigger = rulePane.rule.evaluatedTrigger.rootNode;
      const rootNodeObligations = rulePane.rule.obligations.tree.rootNode;
      const invertTrigger = {
        name: `NOT ${rootNodeTrigger.name}`,
        question: `NOT ${rootNodeTrigger.question}`,
        logicalOperator: LogicalOperator.NOT,
        subConditions: [rootNodeTrigger],
        sourceRules: [],
      };
      const splicedRootNode = {
        name: rulePane.rule.name,
        question: rulePane.rule.name,
        logicalOperator: LogicalOperator.OR,
        subConditions: [invertTrigger, rootNodeObligations],
        sourceRules: [],
      };
      const { nodes: ruleNodes, edges: ruleEdges } = convertToRF(
        sanitizedId,
        conditionNodeToRepresentation(splicedRootNode)
      );
      const { nodes, edges } = {
        nodes: [ruleGroupNode, ...acc.nodes, ...ruleNodes],
        edges: [...acc.edges, ...ruleEdges],
      };
      // First, assign leaf values from questionAnswers
      const nodesWithLeafVals = nodes.map((n) => {
        const v = questionAnswers.find(
          (v) => v.question === (n.data as RFNodeData).question
        );
        return {
          ...n,
          data: {
            ...n.data,
            value: v?.value ?? Outcome.UNDECIDED,
            reasoning: v?.reasoning ?? "",
          },
        };
      });
      // Then propagate values up to parent nodes, respecting propagateWhen
      const nodesWithPropagatedVals = propagateValuesUp(
        nodesWithLeafVals,
        edges
      );
      return { nodes: nodesWithPropagatedVals, edges };
    }
    case PaneType.DEFINITION: {
      const definitionPane = pane as DefinitionPane;
      const definitionGroupNode = {
        id: sanitizedId,
        data: {
          productTreeId: definitionPane.definition.name,
          type: pane.paneType,
          paneId: pane.id,
          blurbInformation: "",
          hasTree: true,
        },
        ...baseGroupElements,
      };
      const rootNode = definitionPane.definition.rootNode;
      const rootNodeAny = rootNode as any;
      const hasSubConditions = ((rootNode as InternalNodeType).subConditions ?? rootNodeAny.sub_conditions)?.length > 0;
      const { nodes: definitionNodes, edges: definitionEdges } =
        hasSubConditions
          ? convertToRF(
            sanitizedId,
            conditionNodeToRepresentation(
              rootNode,
              definitionPane.explanation
            )
          )
          : convertToRF(
            sanitizedId,
            conditionNodeToRepresentation({
              name: definitionPane.explanation,
              question: definitionPane.explanation,
              logicalOperator: LogicalOperator.AND,
              subConditions: [rootNode],
              sourceRules: [],
            })
          );
      const { nodes, edges } = {
        nodes: [definitionGroupNode, ...acc.nodes, ...definitionNodes],
        edges: [...acc.edges, ...definitionEdges],
      };


      // Build a lookup from question text to the best question-answer entry.
      // When the same question appears multiple times, prefer the entry that
      // has reasoning attached so it isn't silently discarded by picking an
      // earlier entry that happens to lack it.
      const qaBestByQuestion = new Map<string, (typeof questionAnswers)[number]>();
      for (const qa of questionAnswers) {
        const existing = qaBestByQuestion.get(qa.question);
        if (!existing || (!existing.reasoning && qa.reasoning)) {
          qaBestByQuestion.set(qa.question, qa);
        }
      }


      // Assign leaf values from questionAnswers
      const nodesWithLeafVals = nodes.map((n) => {
        const v = qaBestByQuestion.get(
          (n.data as RFNodeData).question
        );
        return {
          ...n,
          data: {
            ...n.data,
            value: v?.value ?? Outcome.UNDECIDED,
            reasoning: v?.reasoning ?? "",
          },
        };
      });
      // Then propagate values up to parent nodes, respecting propagateWhen
      const nodesWithPropagatedVals = propagateValuesUp(
        nodesWithLeafVals,
        edges
      );
      return { nodes: nodesWithPropagatedVals, edges };
    }
    default: {
      return acc;
    }
  }
};
